import { HubConnection, HubConnectionBuilder, LogLevel } from '@microsoft/signalr';
import { useEffect, useRef, useState } from 'react';

interface ProductNotification {
  updateType: string;
  customerProductId: string;
  dealId: string;
  contactId: string;
  updatedAt: string;
  message: string;
}

export const useSignalR = (
  apiUrl: string | undefined,
  dealId: string | undefined
) => {
  const [notifications, setNotifications] = useState<ProductNotification[]>([]);
  const [isConnected, setIsConnected] = useState(false);
  const connectionRef = useRef<HubConnection | null>(null);

  useEffect(() => {
    if (!apiUrl || !dealId) return;
    const connection = new HubConnectionBuilder()
      .withUrl(`${apiUrl}hubs/portalproduct`)
      .withAutomaticReconnect()
      .configureLogging(LogLevel.Debug)
      .build();

    connectionRef.current = connection;
    connection.on(
      'ReceiveProductUpdate',
      (notification: ProductNotification) => {
        setNotifications((prev) => [...prev, notification]);
      }
    );

    connection.onreconnecting((error) => {
      console.warn('SignalR reconnecting...', error);
      setIsConnected(false);
    });

    connection.onreconnected(async () => {
      console.log('SignalR reconnected');
      try {
        await connection.invoke('JoinDealGroup', dealId);
        setIsConnected(true);
      } catch (err) {
        console.error('Failed to rejoin group', err);
      }
    });

    connection.onclose((error) => {
      console.warn('SignalR disconnected', error);
      setIsConnected(false);
    });

    const startConnection = async () => {
      try {
        await connection.start();
        console.log('SignalR connected');
        await connection.invoke('JoinDealGroup', dealId);
        setIsConnected(true);
      } catch (err) {
        console.error('SignalR connection error:', err);
      }
    };

    startConnection();

    return () => {
      const stopConnection = async () => {
        try {
          if (connection.state === 'Connected') {
            await connection.invoke('LeaveDealGroup', dealId);
          }
        } catch (err) {
          console.warn('Failed to leave group', err);
        }

        try {
          await connection.stop();
        } catch (err) {
          console.warn('Failed to stop connection', err);
        }
      };

      stopConnection();
    };
  }, [apiUrl, dealId]);

  return {
    notifications,
    isConnected,
  };
};
